import { useRef } from 'react';
import { ChevronLeft, ChevronRight, ImagePlus, Loader2, X } from 'lucide-react';
import { AuthImage } from '@/components/auth-image';
import { cn } from '@/lib/utils';
import { form_error_message } from '@/lib/forms';
import {
  use_catalog_images,
  use_delete_image,
  use_reorder_image,
  use_upload_image,
} from '@/features/catalog-image/hooks';
import { MAX_IMAGES_PER_OWNER, type CatalogImageOwnerType } from '@/features/catalog-image/types';

/**
 * Up to 5 ordered photos for one catalog entity (menu item / offering /
 * service). Thumbnails with remove + reorder arrows, plus an add tile.
 */
export function ImageGalleryEditor({
  owner_type,
  owner_id,
  label = 'Photos',
  className,
}: {
  owner_type: CatalogImageOwnerType;
  owner_id: string;
  label?: string;
  className?: string;
}) {
  const list_q = use_catalog_images(owner_type, owner_id);
  const upload_m = use_upload_image(owner_type, owner_id);
  const delete_m = use_delete_image(owner_type, owner_id);
  const reorder_m = use_reorder_image(owner_type, owner_id);
  const file_ref = useRef<HTMLInputElement>(null);

  const images = list_q.data ?? [];
  const full = images.length >= MAX_IMAGES_PER_OWNER;
  const busy = upload_m.isPending;

  async function on_pick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    try {
      await upload_m.mutateAsync(file);
    } catch {
      /* surfaced via upload_m.error below */
    }
  }

  // Swap display_order with a neighbour, then persist both.
  async function move(index: number, dir: -1 | 1) {
    const next = index + dir;
    if (next < 0 || next >= images.length) return;
    const a = images[index];
    const b = images[next];
    await Promise.all([
      reorder_m.mutateAsync({ id: a.id, display_order: next }),
      reorder_m.mutateAsync({ id: b.id, display_order: index }),
    ]);
  }

  return (
    <div className={cn('flex flex-col gap-1.5', className)} data-testid={`gallery-editor-${owner_id}`}>
      <span className="text-sm font-semibold">
        {label} <span className="font-normal text-muted-foreground">({images.length}/{MAX_IMAGES_PER_OWNER})</span>
      </span>
      <div className="flex flex-wrap gap-2">
        {images.map((img, i) => (
          <div
            key={img.id}
            className="group relative h-20 w-20 overflow-hidden rounded-input border-2 border-brand-outline-variant/20 bg-muted shadow-clay-sm"
            data-testid={`gallery-thumb-${img.id}`}
          >
            <AuthImage path={img.thumb_path} alt="" className="h-full w-full object-cover" />
            <button
              type="button"
              onClick={() => delete_m.mutate(img.id)}
              aria-label="Remove photo"
              data-testid={`gallery-remove-${img.id}`}
              className="absolute right-0.5 top-0.5 grid h-6 w-6 place-items-center rounded-full bg-foreground/70 text-white opacity-0 transition-opacity hover:bg-destructive group-hover:opacity-100"
            >
              <X className="h-3.5 w-3.5" />
            </button>
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-foreground/55 px-0.5 py-0.5 opacity-0 transition-opacity group-hover:opacity-100">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move left" className="grid h-5 w-5 place-items-center rounded text-white disabled:opacity-30">
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === images.length - 1} aria-label="Move right" className="grid h-5 w-5 place-items-center rounded text-white disabled:opacity-30">
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}

        {!full && (
          <button
            type="button"
            onClick={() => file_ref.current?.click()}
            disabled={busy}
            data-testid={`gallery-add-${owner_id}`}
            className="grid h-20 w-20 place-items-center rounded-input border-2 border-dashed border-brand-outline-variant/40 bg-secondary/40 text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary"
          >
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
          </button>
        )}
        <input ref={file_ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={on_pick} data-testid={`gallery-input-${owner_id}`} />
      </div>
      {upload_m.error && (
        <p className="text-xs text-destructive" role="alert">
          {form_error_message(upload_m.error, 'Could not upload that image.')}
        </p>
      )}
    </div>
  );
}
